import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, Briefcase, ClipboardCheck, CirclePlay, DoorOpen, FileText, FlaskConical, Gamepad2, Image, Lightbulb,
  Link2, MessagesSquare, MousePointerClick, Package, Pill, Share2, Target, TriangleAlert, Warehouse,
} from "lucide-react";
import Footer from "../components/Footer";
import "./LandingPage.css";

// What's in the hero's scene: a drawn room from the Game Builder's own picture library, with the things a player can act
// on marked the way the builder marks them. x/y are percentages of the 3:2 picture; `flip` puts the label on the left; `wide` ones only show on wider screens.
const HOTSPOTS = [
  { icon: DoorOpen, kind: "Door", name: "To the loading bay", x: 53, y: 36, flip: true },
  { icon: Package, kind: "Object", name: "Steel shelving", x: 9, y: 48, wide: true },
  { icon: TriangleAlert, kind: "Hazard", name: "Open inspection pit", x: 47, y: 84 },
];

const INDUSTRIES = [
  { img: "warehouse", icon: Warehouse, title: "Warehouse & Logistics", text: "Safety checks, equipment handling and closing-up routines." },
  { img: "pharmacy", icon: Pill, title: "Healthcare & Pharmacy", text: "Dispensing, patient interactions and compliance steps." },
  { img: "lab", icon: FlaskConical, title: "Laboratories", text: "Prep, handling and clean-down before and after a run." },
  { img: "office", icon: Briefcase, title: "Offices & Corporate", text: "Policies, security and workplace procedures." },
];

const STEPS = [
  { icon: FileText, title: "Start from your procedure", text: "Upload an SOP, a manual or a policy - or describe the job in a short chat." },
  { icon: MessagesSquare, title: "Answer what it can't know", text: "Who plays, what they must do, and the mistakes people really make." },
  { icon: Gamepad2, title: "Review, then build", text: "Check how each mission is won. The studio builds a scene the player walks into." },
  { icon: Share2, title: "Share a link", text: "Your people play in the browser - no account, no install." },
];

const FEATURES = [
  { icon: Lightbulb, title: "AI-powered creation", text: "Describe the job; the studio drafts the missions, the room and the things in it." },
  { icon: ClipboardCheck, title: "You approve every step", text: "Nothing is built until you've checked how each mission is won and lost." },
  { icon: BarChart3, title: "See how people did", text: "Plays, finishes, the usual time and the mistakes made most often." },
  { icon: Link2, title: "Nothing to install", text: "Learners open a link on a laptop or phone and play straight away." },
];

const PROMISES = [
  { icon: MousePointerClick, text: "No coding required" },
  { icon: Image, text: "Drawn scenes of real workplaces" },
  { icon: Share2, text: "Shared by link, played in the browser" },
];

// The public front page (logistics-workflow serves a static landing.html; a React page keeps this app in one build).
export default function LandingPage() {
  return (
    <div className="lp">
      <header className="lp-nav">
        <Link to="/" className="lp-brand"><img src="/logo.svg" alt="" /> SimulateJob</Link>
        <nav className="lp-links" aria-label="Sections">
          <a href="#how">How it works</a>
          <a href="#industries">Industries</a>
          <Link to="/book-demo">Book a demo</Link>
        </nav>
        <div className="lp-actions">
          <Link to="/login" className="lp-signin">Sign in</Link>
          <Link to="/register" className="lp-btn lp-btn-primary">Get started <ArrowRight size={18} /></Link>
        </div>
      </header>

      <section className="lp-hero">
        <div className="lp-hero-text">
          <p className="lp-eyebrow">Real work. Real consequences.</p>
          <h1>Create interactive job simulations <span>with AI.</span></h1>
          <p className="lp-lead">
            Turn your procedures, policies and expertise into hands-on scenes where your people explore, make decisions and
            see the consequences - not a quiz, the job itself.
          </p>
          <div className="lp-cta">
            <Link to="/register" className="lp-btn lp-btn-primary">Start building free <ArrowRight size={18} /></Link>
            <Link to="/try-demo" className="lp-btn lp-btn-ghost"><CirclePlay size={20} /> Try the demo</Link>
          </div>
          <ul className="lp-promises">
            {PROMISES.map(({ icon: Icon, text }) => <li key={text}><Icon size={22} aria-hidden="true" /> {text}</li>)}
          </ul>
        </div>

        <div className="lp-scene" aria-label="A warehouse scene from a training game, with the things the player can act on marked">
          <div className="lp-stage">
            <img src="/assets/landing/hero-warehouse.jpg" alt="" />
            {HOTSPOTS.map(({ icon: Icon, kind, name, x, y, flip, wide }) => (
              <div key={name} className={`lp-hotspot${flip ? " is-flipped" : ""}${wide ? " is-wide-only" : ""}`} style={{ left: `${x}%`, top: `${y}%` }}>
                <span className="lp-dot" aria-hidden="true" />
                <div className="lp-tag"><Icon size={18} aria-hidden="true" /><div><strong>{kind}</strong><span>{name}</span></div></div>
              </div>
            ))}
          </div>
          <div className="lp-goal">
            <div className="lp-goal-head"><Target size={20} aria-hidden="true" /> <strong>Mission goal</strong></div>
            <p>Close the bay at the end of the shift: cover the pit, clear the shelving and lock the loading-bay door.</p>
            <div className="lp-goal-count">0 / 3 done</div>
          </div>
        </div>
      </section>

      <section className="lp-band" id="industries">
        <div className="lp-wrap">
          <div className="lp-band-head">
            <div>
              <p className="lp-eyebrow">Built for real-world roles</p>
              <h2>Start from your own procedure, in any workplace.</h2>
            </div>
            <Link to="/try-demo" className="lp-more">See a finished game <ArrowRight size={18} /></Link>
          </div>
          <div className="lp-cards">
            {INDUSTRIES.map(({ img, icon: Icon, title, text }) => (
              <article key={title} className="lp-card">
                <div className="lp-card-media">
                  <div className="lp-card-img"><img src={`/assets/landing/card-${img}.jpg`} alt="" loading="lazy" /></div>
                  <span className="lp-card-badge"><Icon size={18} aria-hidden="true" /></span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-wrap lp-how" id="how">
        <p className="lp-eyebrow">How it works</p>
        <h2>From a document to a game your team can play.</h2>
        <ol className="lp-steps">
          {STEPS.map(({ icon: Icon, title, text }, n) => (
            <li key={title}>
              <span className="lp-step-n">{n + 1}</span>
              <Icon size={24} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="lp-features">
        <div className="lp-wrap lp-features-row">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="lp-feature">
              <Icon size={30} aria-hidden="true" />
              <div><h3>{title}</h3><p>{text}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-wrap lp-closing">
        <h2>See it with your own procedure.</h2>
        <p>We&rsquo;ll turn one of your SOPs into a playable mission on the call.</p>
        <div className="lp-cta">
          <Link to="/book-demo" className="lp-btn lp-btn-primary">Book a demo <ArrowRight size={18} /></Link>
          <Link to="/register" className="lp-btn lp-btn-ghost">Create your team&rsquo;s account</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
